import { ReservedTimeNotice } from '@components/BookingFlow/Stationary/ReservedTimeNotice';
import { StationaryLoginOrderDetails } from '@components/BookingFlow/Stationary/StationaryLoginOrderDetails';
import { StationaryReservationExpiredBanner } from '@components/BookingFlow/Stationary/StationaryReservationExpiredBanner';
import { ProposalContactPhoneNotice } from '@components/BookingProposal/ProposalContactPhoneNotice';
import { getProposalSummaryDecimals } from '@components/BookingProposal/proposalSummaryDecimals';
import { CheckoutLoginStep } from '@components/Login/CheckoutLoginStep';
import { plural } from '@lingui/core/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { customerRoutes, formatCurrencyAmount } from 'noddi-util';
import { useAuthContext, useCancelHoldOnExpiry } from 'noddi-web';
import { Navigate } from 'react-router-dom';
import { useStationaryProposalCheckout } from '../../hooks/useStationaryProposalCheckout';
import { useTrackProposalStep } from '../../hooks/useTrackProposalStep';
import {
  useBookingProposalSlug,
  useBookingProposalStationaryReservation,
  useBookingProposalUserGroupId
} from '../../stores/bookingProposalStore';
import { showsSuggestedProposalTimes } from '../../utils/stationaryProposalPolicy';
import { getStationaryProposalBackPath, getStationaryProposalFirstStepPath } from '../../utils/stationaryProposalSteps';

/**
 * Login as its own step between picking a time and the summary, so the summary
 * is a pure review-and-pay page. The bay is already held here — the recap and
 * the draining countdown say so, since logging in costs the customer time they
 * can see.
 */
export const StationaryProposalLogin = () => {
  const slug = useBookingProposalSlug();
  const reservation = useBookingProposalStationaryReservation();
  const proposalUserGroupId = useBookingProposalUserGroupId();
  const { isLoggedIn } = useAuthContext();
  const isReservationExpired = useCancelHoldOnExpiry(reservation);
  const { data: proposal } = noddiAsync.useGet({
    type: URLKeys.getBookingProposalBySlug,
    input: { slug }
  });
  const checkout = useStationaryProposalCheckout(proposal);
  const usesSuggestedTimes = proposal != null && showsSuggestedProposalTimes(proposal.serviceDepartment.brand.domain);
  // Both redirects leave without rendering the gate, so neither is a view.
  useTrackProposalStep('login', !!reservation && !isLoggedIn);

  // Nothing held means they never picked a time — send them to the first step.
  if (!reservation) {
    return <Navigate to={getStationaryProposalFirstStepPath(slug, { usesSuggestedTimes })} replace />;
  }

  const hasOrderRecap = !checkout.isShoppingCartPending && !checkout.shoppingCartError;
  const total = formatCurrencyAmount({
    money: checkout.orderTotalPrice,
    decimals: getProposalSummaryDecimals([checkout.orderTotalPrice.amount])
  });

  return (
    <CheckoutLoginStep
      nextPath={customerRoutes.stationaryProposalSummary.getPath({ slug })}
      backPath={getStationaryProposalBackPath({ step: 'login', slug, usesSuggestedTimes })}
      // Same width as every other step of the flow — a narrower shell here makes the page jump between steps.
      className='max-w-screen-md'
      // No profile picker: the proposal owns its group (useProposalUserGroupSync selects it).
      userGroupId={proposalUserGroupId}
      notice={
        <>
          {checkout.showsLoggedOutContactPhoneNotice && (
            <ProposalContactPhoneNotice phoneDisplay={checkout.displayedInvalidUserPhone} />
          )}
          {!isReservationExpired && <ReservedTimeNotice reservation={reservation} />}
        </>
      }
      blockedBy={
        isReservationExpired && (
          <StationaryReservationExpiredBanner
            timeWindowPath={getStationaryProposalFirstStepPath(slug, { usesSuggestedTimes })}
          />
        )
      }
      // The recap is optional here: a slow or failed cart must never hold up the login.
      order={
        hasOrderRecap
          ? {
              // The car leads the expanded details, so the collapsed line counts services instead.
              description: plural(checkout.orderLineItems.length, { one: '# service', other: '# services' }),
              total,
              details: (
                <StationaryLoginOrderDetails
                  carDisplayName={checkout.carDisplayName}
                  lines={checkout.orderLineItems}
                  locationName={checkout.locationName}
                  total={total}
                />
              )
            }
          : undefined
      }
    />
  );
};
